import Popover from "@mui/material/Popover";
import { useTranslation } from "react-i18next";

import FilterIcon from "assets/icons/Filters.svg?react";

import { useMenu } from "components/ActionMenu/useMenu";
import ContextMenu from "components/ContextMenu/ContextMenu";
import ContextMenuDivider from "components/ContextMenu/ContextMenuDivider";
import ContextMenuItem from "components/ContextMenu/ContextMenuItem";

export enum SearchFilterType {
  All = "all",
  Databases = "databases",
  Tables = "tables",
  Views = "views",
  Columns = "columns",
  Indexes = "indexes",
}

export enum SearchSortType {
  Alphabetical = "alphabetical",
  LastModified = "last_modified",
  DateCreated = "date_created",
}

type SearchCounts = {
  total: number;
  databases: number;
  tables: number;
  views: number;
  columns: number;
  indexes: number;
};

type Props = {
  filterType: SearchFilterType;
  sortType: SearchSortType;
  counts: SearchCounts;
  onFilterChange: (next: SearchFilterType) => void;
  onSortChange: (next: SearchSortType) => void;
};

export const DatabaseSearchMenu = (props: Props) => {
  const { filterType, sortType, counts, onFilterChange, onSortChange } = props;
  const { openMenu, closeMenu, menuElement, iconElement } = useMenu();
  const { t } = useTranslation();

  return (
    <>
      <div
        ref={iconElement}
        onClick={openMenu}
        data-testid="database-search-menu-trigger"
      >
        <FilterIcon />
      </div>
      <Popover
        open={Boolean(menuElement)}
        anchorEl={menuElement}
        onClose={closeMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        role="menu"
      >
        <ContextMenu>
          <ContextMenuItem
            text={t("workspace.search_menu.all")}
            onClick={() => {
              onFilterChange(SearchFilterType.All);
              closeMenu();
            }}
            checked={filterType === SearchFilterType.All}
            secondaryText={String(counts.total)}
          />
          <ContextMenuItem
            text={t("workspace.search_menu.databases")}
            onClick={() => {
              onFilterChange(SearchFilterType.Databases);
              closeMenu();
            }}
            checked={filterType === SearchFilterType.Databases}
            secondaryText={String(counts.databases)}
          />
          <ContextMenuItem
            text={t("workspace.search_menu.tables")}
            onClick={() => {
              onFilterChange(SearchFilterType.Tables);
              closeMenu();
            }}
            checked={filterType === SearchFilterType.Tables}
            secondaryText={String(counts.tables)}
          />
          <ContextMenuItem
            text={t("workspace.search_menu.views")}
            onClick={() => {
              onFilterChange(SearchFilterType.Views);
              closeMenu();
            }}
            checked={filterType === SearchFilterType.Views}
            secondaryText={String(counts.views)}
          />
          <ContextMenuItem
            text={t("workspace.search_menu.columns")}
            onClick={() => {
              onFilterChange(SearchFilterType.Columns);
              closeMenu();
            }}
            checked={filterType === SearchFilterType.Columns}
            secondaryText={String(counts.columns)}
          />
          <ContextMenuItem
            text={t("workspace.search_menu.indexes")}
            onClick={() => {
              onFilterChange(SearchFilterType.Indexes);
              closeMenu();
            }}
            checked={filterType === SearchFilterType.Indexes}
            secondaryText={String(counts.indexes)}
          />
          <ContextMenuDivider />
          <ContextMenuItem
            text={t("workspace.search_menu.sort_by")}
            disabled
          />
          <ContextMenuItem
            text={t("workspace.search_menu.alphabetical")}
            onClick={() => {
              onSortChange(SearchSortType.Alphabetical);
              closeMenu();
            }}
            checked={sortType === SearchSortType.Alphabetical}
          />
          <ContextMenuItem
            text={t("workspace.search_menu.last_modified")}
            onClick={() => {
              onSortChange(SearchSortType.LastModified);
              closeMenu();
            }}
            checked={sortType === SearchSortType.LastModified}
          />
          <ContextMenuItem
            text={t("workspace.search_menu.date_created")}
            onClick={() => {
              onSortChange(SearchSortType.DateCreated);
              closeMenu();
            }}
            checked={sortType === SearchSortType.DateCreated}
          />
        </ContextMenu>
      </Popover>
    </>
  );
};
